// Client-side AES-256-GCM encryption for Blossom media uploads.
// Keys are encoded in URL fragments (#aes256gcm:hexKey:hexIv) which
// are never sent to the server per HTTP spec.
// Uses Web Crypto API for hardware-accelerated AES-NI (~170x faster than pure JS ciphers).

const FRAGMENT_PREFIX = "aes256gcm:";
const MAX_CACHE = 100;

// LRU cache: encrypted URL → decrypted object URL
const decryptedCache = new Map<string, string>();

function evictOldest() {
  if (decryptedCache.size <= MAX_CACHE) return;
  const first = decryptedCache.keys().next().value!;
  const url = decryptedCache.get(first);
  if (url) URL.revokeObjectURL(url);
  decryptedCache.delete(first);
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function encryptFile(file: File): Promise<{ encrypted: File; key: string; iv: string }> {
  const keyBytes = crypto.getRandomValues(new Uint8Array(32));
  const ivBytes = crypto.getRandomValues(new Uint8Array(12));

  const cryptoKey = await crypto.subtle.importKey("raw", keyBytes, "AES-GCM", false, ["encrypt"]);
  const plaintext = await file.arrayBuffer();
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv: ivBytes }, cryptoKey, plaintext);

  // Preserve original extension so Blossom URL keeps it (needed for isImageURL/isVideoURL detection)
  const encrypted = new File([ciphertext], file.name, { type: "application/octet-stream" });

  return {
    encrypted,
    key: bytesToHex(keyBytes),
    iv: bytesToHex(ivBytes),
  };
}

export async function decryptBlob(data: ArrayBuffer, hexKey: string, hexIv: string): Promise<ArrayBuffer> {
  const keyBuf = hexToBytes(hexKey).buffer as ArrayBuffer;
  const ivBuf = hexToBytes(hexIv).buffer as ArrayBuffer;
  const cryptoKey = await crypto.subtle.importKey("raw", keyBuf, "AES-GCM", false, ["decrypt"]);
  return crypto.subtle.decrypt({ name: "AES-GCM", iv: ivBuf }, cryptoKey, data);
}

export function buildEncryptedUrl(baseUrl: string, key: string, iv: string): string {
  return `${baseUrl}#${FRAGMENT_PREFIX}${key}:${iv}`;
}

export function parseEncryptedUrl(url: string): { baseUrl: string; key: string; iv: string } | null {
  const hashIdx = url.indexOf("#" + FRAGMENT_PREFIX);
  if (hashIdx === -1) return null;

  const baseUrl = url.substring(0, hashIdx);
  const fragment = url.substring(hashIdx + 1 + FRAGMENT_PREFIX.length);
  const colonIdx = fragment.indexOf(":");
  if (colonIdx === -1) return null;

  const key = fragment.substring(0, colonIdx);
  const iv = fragment.substring(colonIdx + 1);
  // AES-256-GCM: 32-byte key (64 hex), 12-byte IV (24 hex)
  if (key.length !== 64 || iv.length !== 24) return null;

  return { baseUrl, key, iv };
}

export function isEncryptedUrl(url: string): boolean {
  return url.includes("#" + FRAGMENT_PREFIX);
}

/** Fetch an encrypted blob, decrypt it, and return an object URL. Caches results. */
export async function fetchAndDecrypt(url: string): Promise<string> {
  const cached = decryptedCache.get(url);
  if (cached) {
    // Move to end (LRU refresh)
    decryptedCache.delete(url);
    decryptedCache.set(url, cached);
    return cached;
  }

  const parsed = parseEncryptedUrl(url);
  if (!parsed) throw new Error("Not an encrypted URL");

  const response = await fetch(parsed.baseUrl);
  if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

  const encrypted = await response.arrayBuffer();
  const plaintext = await decryptBlob(encrypted, parsed.key, parsed.iv);

  // Infer MIME type from original file extension
  const mime = guessMimeType(parsed.baseUrl);
  const objectUrl = URL.createObjectURL(new Blob([plaintext], mime ? { type: mime } : undefined));

  decryptedCache.set(url, objectUrl);
  evictOldest();

  return objectUrl;
}

function guessMimeType(url: string): string | undefined {
  const path = new URL(url).pathname.toLowerCase();
  if (path.endsWith(".jpg") || path.endsWith(".jpeg")) return "image/jpeg";
  if (path.endsWith(".png")) return "image/png";
  if (path.endsWith(".gif")) return "image/gif";
  if (path.endsWith(".webp")) return "image/webp";
  if (path.endsWith(".avif")) return "image/avif";
  if (path.endsWith(".svg")) return "image/svg+xml";
  if (path.endsWith(".mp4")) return "video/mp4";
  if (path.endsWith(".webm")) return "video/webm";
  if (path.endsWith(".mov")) return "video/quicktime";
  if (path.endsWith(".mkv")) return "video/x-matroska";
  return undefined;
}
